// =============================================================================
// SimSense Platform — Centralized Unit Conversions
// =============================================================================

export type UnitSystem = "standard" | "metric";

/**
 * Converts value based on user preference.
 * Input standard is usually SI/iRacing raw (m/s, Celsius, Liters).
 */
export const units = {
  /** Speed: m/s to mph or kph */
  speed: (ms: number, system: UnitSystem = "standard"): string => {
    if (ms == null || isNaN(ms)) return "—";
    if (system === "metric") {
      return `${Math.round(ms * 3.6)} kph`;
    }
    return `${Math.round(ms * 2.23694)} mph`;
  },

  /** Temp: Celsius to F or C */
  temp: (c: number, system: UnitSystem = "standard"): string => {
    if (c == null || isNaN(c)) return "—";
    if (system === "metric") {
      return `${Math.round(c)}°C`;
    }
    return `${Math.round((c * 9) / 5 + 32)}°F`;
  },

  /** Pressure: kPa to PSI or Bar */
  pressure: (kpa: number, system: UnitSystem = "standard"): string => {
    if (kpa == null || isNaN(kpa)) return "—";
    if (system === "metric") {
      return `${(kpa / 100).toFixed(2)} bar`;
    }
    return `${Math.round(kpa * 0.145038)} psi`;
  },

  /** Weight/Force: N to Lbs or Kg */
  weight: (newtons: number, system: UnitSystem = "standard"): string => {
    if (newtons == null || isNaN(newtons)) return "—";
    if (system === "metric") {
      return `${Math.round(newtons / 9.80665)} kg`;
    }
    return `${Math.round(newtons * 0.224809)} lbs`;
  },

  /** Delta: s to s (just formatting) */
  delta: (seconds: number): string => {
    const sign = seconds > 0 ? "+" : "";
    return `${sign}${seconds.toFixed(3)}s`;
  }
};
